import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  LayoutDashboard,
  LogOut,
  FileBarChart,
  ShoppingBag,
  Activity,
  ClipboardList,
  Zap,
  Sparkles,
  Map as MapIcon,
  Building2,
  Shield,
  BookOpen,
  ChevronDown,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import logoFeo from "@/assets/feo/logo-feo.png";
import adminLogoDarkAsset from "@/assets/feo/brand/logo-horizontal-colorido-branco.webp.asset.json";
import adminLogoLightAsset from "@/assets/feo/brand/logo-horizontal-colorido-escuro.webp.asset.json";
import { AdminGate } from "@/components/admin/AdminGate";
import { BLACKFEO_MODULOS } from "@/components/admin/BlackfeoAdminShell";
import { getMyAccess, resetMemberPassword } from "@/lib/admin/access.functions";
import { ADMIN_PAGES, getAdminPagesByGroup, resolveAccessRoute, hasPageAccess, type AdminPage } from "@/lib/admin/pages";
void Label;

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin — Acesso" },
      { name: "description", content: "Painel de controle FEO: operação, campanhas e segurança dos grupos com o FEO Guard." },
      { property: "og:title", content: "Admin — Painel de Controle FEO" },
      { property: "og:description", content: "Acesso da equipe à operação, campanhas e ao FEO Guard." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminIndexPage,
});

function AdminIndexPage() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [access, setAccess] = useState<{ isMaster: boolean; pages: string[]; status: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadAccess() {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!data.session) {
        setAuthed(false);
        setAccess(null);
        setChecking(false);
        return;
      }
      setAuthed(true);
      try {
        const myAccess = await getMyAccess();
        if (cancelled) return;
        setAccess({ isMaster: myAccess.isMaster, pages: myAccess.pages, status: myAccess.status });
      } catch {
        if (cancelled) return;
        setAccess({ isMaster: false, pages: [], status: null });
      }
      setChecking(false);
    }

    loadAccess();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        setAuthed(false);
        setAccess(null);
        return;
      }
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        loadAccess();
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);


  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando...</div>;
  }

  if (authed && access && (access.isMaster || access.status === "active")) {
    return (
      <AdminGate>
        <AdminHub access={access} />
      </AdminGate>
    );
  }

  return (
    <AdminLogin
      onAuthed={(a) => {
        setAuthed(true);
        setAccess(a);
      }}
    />
  );
}

function AdminLogin({
  onAuthed,
}: {
  onAuthed: (access: { isMaster: boolean; pages: string[]; status: string | null }) => void;
}) {
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [deniedMessage, setDeniedMessage] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) throw error;

      const myAccess = await getMyAccess();
      if (!myAccess.isMaster && myAccess.status !== "active") {
        setDeniedMessage(true);
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      toast.success("Acesso liberado");
      onAuthed({ isMaster: myAccess.isMaster, pages: myAccess.pages, status: myAccess.status });

    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Credenciais inválidas");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetMemberPassword({ data: { email: email.trim() } });
      toast.success("Link de redefinição enviado para o e-mail.");
      setMode("login");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao enviar e-mail");
    } finally {
      setLoading(false);
    }
  };

  if (deniedMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b0a1f] via-[#13123a] to-[#1a1850] px-4">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl text-center">
          <h1 className="text-2xl font-bold text-white tracking-tight uppercase mb-2">Acesso não liberado</h1>
          <p className="text-admin-accent font-semibold uppercase tracking-wider mb-6">FALAR COM RUPEL PARA GARANTIR SEU ACESSO</p>
          <button onClick={() => setDeniedMessage(false)} className="text-sm text-white/60 hover:text-white underline">
            Voltar para o login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b0a1f] via-[#13123a] to-[#1a1850] px-4 relative overflow-hidden">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl relative animate-scale-in">
        <div className="flex flex-col items-center mb-6 animate-fade-in">
          <img src={logoFeo} alt="Fisio em Ortopedia" className="h-14 w-auto drop-shadow-[0_4px_20px_rgba(245,176,34,0.3)]" />
          <h1 className="mt-4 text-2xl font-bold text-white tracking-tight uppercase">Painel de Controle</h1>
          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-admin-accent">Acesso restrito</p>
        </div>

        <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10 mb-6">
            <TabsTrigger value="login" className="data-[state=active]:bg-[#f5b022] data-[state=active]:text-[#13123a] text-white/70">Entrar</TabsTrigger>
            <TabsTrigger value="reset" className="data-[state=active]:bg-[#f5b022] data-[state=active]:text-[#13123a] text-white/70">Senha</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-3">
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="E-mail" className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="Senha" className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
              <Button type="submit" className="w-full bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold shadow-lg shadow-[#f5b022]/20" disabled={loading}>
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="reset">
            <form onSubmit={handleReset} className="space-y-3">
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="E-mail da conta" className="bg-white/5 border-white/10 text-white placeholder:text-white/40" />
              <Button type="submit" className="w-full bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold shadow-lg shadow-[#f5b022]/20" disabled={loading}>
                {loading ? "Enviando..." : "Enviar link de redefinição"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const ICON_MAP: Record<string, React.ElementType> = {
  LayoutDashboard,
  BarChart3,
  Users,
  FileBarChart,
  ShoppingBag,
  Activity,
  ClipboardList,
  Zap,
  Sparkles,
  MapIcon,
  Building2,
  Shield,
  BookOpen,
};

function AdminHub({ access }: { access: { isMaster: boolean; pages: string[] } }) {
  const navigate = useNavigate();
  const [booting, setBooting] = useState(true);
  const [testesAberto, setTestesAberto] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 1800);
    return () => clearTimeout(t);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/admin" });
  };

  const visiblePages = access.isMaster
    ? ADMIN_PAGES.filter((p) => p.route !== "/admin/" && p.route !== "/admin/black")
    : ADMIN_PAGES.filter((p) => access.pages.includes(resolveAccessRoute(p.route)) && p.route !== "/admin/" && p.route !== "/admin/black");


  const grouped = getAdminPagesByGroup();
  const visibleGroups = Object.entries(grouped)
    .map(([group, pages]) => ({
      label: group,
      cards: pages.filter((p) => p.route !== "/admin/feo-guard" && visiblePages.some((vp) => vp.route === p.route)),
    }))
    .filter((g) => g.cards.length > 0);

  // Sempre mostra o card de Controle de Acessos (Configurações) para o master.
  if (access.isMaster) {
    const acessosPage = ADMIN_PAGES.find((p) => p.route === "/admin/acessos")!;
    const config = visibleGroups.find((g) => g.label === "CONFIGURAÇÕES");
    if (config) {
      if (!config.cards.some((c) => c.route === "/admin/acessos")) {
        config.cards.push(acessosPage);
      }
    } else {
      visibleGroups.push({ label: "CONFIGURAÇÕES", cards: [acessosPage] });
    }
  }

  let cardIndex = 0;

  const AdminBrand = ({ className }: { className: string }) => (
    <span className="relative inline-flex shrink-0">
      <img decoding="async" src={adminLogoDarkAsset.url} alt="Fisio em Ortopedia" className={`${className} admin-logo-on-dark`} />
      <img decoding="async" src={adminLogoLightAsset.url} alt="" aria-hidden="true" className={`${className} admin-logo-on-light`} />
    </span>
  );

  return (
    <div className="min-h-screen relative overflow-hidden">
      {booting && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0b0a1f] animate-fade-out" style={{ animationDelay: "1.3s", animationFillMode: "forwards" }}>
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#f5b022]/30 blur-3xl animate-pulse" />
            <AdminBrand className="relative h-24 w-auto animate-scale-in drop-shadow-[0_4px_30px_rgba(245,176,34,0.5)]" />
          </div>
          <div className="mt-8 flex items-center gap-2 text-white/70 text-sm tracking-[0.3em] uppercase animate-fade-in" style={{ animationDelay: "0.4s", animationFillMode: "backwards" }}>
            <span className="h-px w-8 bg-[#f5b022]" />
            Acessando painel
            <span className="h-px w-8 bg-[#f5b022]" />
          </div>
          <div className="mt-6 h-0.5 w-48 bg-white/10 overflow-hidden rounded-full">
            <div className="h-full w-full bg-gradient-to-r from-transparent via-[#f5b022] to-transparent animate-[slide-in-right_1.3s_ease-out]" />
          </div>
        </div>
      )}

      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 0%, rgba(11,10,31,0.45) 100%)" }} />

      <div className="relative px-3 py-8 sm:px-4 sm:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 mb-8 sm:flex sm:items-center sm:justify-between sm:mb-12 animate-fade-in" style={{ animationDelay: "1.5s", animationFillMode: "backwards" }}>
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <AdminBrand className="h-10 sm:h-12 w-auto drop-shadow-[0_4px_20px_rgba(245,176,34,0.3)]" />
              <div className="hidden sm:block h-10 w-px bg-white/10" />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight truncate">Painel de Controle</h1>
                <p className="text-[10px] sm:text-xs text-white/50 mt-0.5 uppercase tracking-widest truncate">Fisio em Ortopedia · Admin</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={handleLogout} className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
                <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>

          <div className="space-y-10">
            {(access.isMaster || hasPageAccess(access.pages, "/admin/black") || hasPageAccess(access.pages, "/admin/feo-guard")) && (
              <section className="black-campaign-vars animate-fade-in" style={{ animationDelay: "1.55s", animationFillMode: "backwards" }}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-xs font-bold tracking-[0.2em] uppercase text-black-campaign-cyan">BLACK 2x1 2026 · 05/10 a 03/11</span>
                  <span className="flex-1 h-px bg-gradient-to-r from-black-campaign-purple/60 via-black-campaign-cyan/30 to-transparent" />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  {([
                    ["/admin/plano-black-2x1", "PLANO DA AÇÃO", "Plano completo, fluxos, links e regras", ClipboardList],
                    ["/admin/black-dashboard", "DASHBOARD DA AÇÃO", "Leads e vendas da Black em tempo real", BarChart3],
                    ["/admin/feo-guard", "FEO GUARD", "Anti-Hacker dos grupos de WhatsApp", Shield],
                  ] as const).filter(([to]) => access.isMaster || hasPageAccess(access.pages, to)).map(([to, title, desc, Icon]) => (
                    <Link key={to} to={to} className={to === "/admin/feo-guard"
                      ? "feo-guard-entry group relative rounded-2xl border border-primary/40 p-6 text-foreground transition-colors overflow-hidden hover:border-primary"
                      : "group relative rounded-2xl border p-6 shadow-xl transition-all duration-300 overflow-hidden backdrop-blur-sm border-black-campaign-purple/50 bg-black-campaign hover:shadow-2xl hover:border-black-campaign-cyan/60"}>
                      <div className={to === "/admin/feo-guard"
                        ? "inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 border border-primary/30 mb-4"
                        : "inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-black-campaign-purple/30 to-black-campaign-cyan/10 border border-black-campaign-lilac/30 mb-4"}>
                        <Icon className={`h-6 w-6 ${to === "/admin/feo-guard" ? "text-primary" : "text-black-campaign-cyan"}`} />
                      </div>
                      <h2 className={`text-base font-bold ${to === "/admin/feo-guard" ? "text-foreground" : "text-white tracking-wide"}`}>{title}</h2>
                      <p className={`mt-1.5 text-sm leading-relaxed ${to === "/admin/feo-guard" ? "text-muted-foreground" : "text-white/60"}`}>{desc}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {(access.isMaster || hasPageAccess(access.pages, "/admin/black-november")) && (
              <section className="black-campaign-vars blackfeo-campaign-vars animate-fade-in" style={{ animationDelay: "1.58s", animationFillMode: "backwards" }}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-xs font-bold tracking-[0.2em] uppercase text-black-campaign-cyan">BLACKFEO 2026 · 04/11 a 30/11</span>
                  <span className="flex-1 h-px bg-gradient-to-r from-black-campaign-purple/60 via-black-campaign-cyan/30 to-transparent" />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  {BLACKFEO_MODULOS.filter(({ to }) => access.isMaster || hasPageAccess(access.pages, to)).map(({ to, title, desc, icon: Icon }) => (
                    <Link key={to} to={to} className="group relative rounded-2xl border p-6 shadow-xl transition-all duration-300 overflow-hidden backdrop-blur-sm border-black-campaign-purple/50 bg-black-campaign hover:shadow-2xl hover:border-black-campaign-cyan/60">
                      <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-black-campaign-purple/30 to-black-campaign-cyan/10 border border-black-campaign-lilac/30 mb-4">
                        <Icon className="h-6 w-6 text-black-campaign-cyan" />
                      </div>
                      <h2 className="text-base font-bold text-white tracking-wide uppercase">{title}</h2>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/60">{desc}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {visibleGroups.map((section, sectionIdx) => (
              <section key={section.label} className="animate-fade-in" style={{ animationDelay: `${1.6 + sectionIdx * 0.15}s`, animationFillMode: "backwards" }}>
                <div className="flex items-center gap-4 mb-5">
                  {section.label === "TESTES" ? (
                    <button type="button" onClick={() => setTestesAberto((v) => !v)} className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-admin-accent" aria-expanded={testesAberto}>
                      {section.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${testesAberto ? "rotate-180" : ""}`} />
                    </button>
                  ) : (
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-admin-accent">{section.label}</span>
                  )}
                  <span className="flex-1 h-px bg-gradient-to-r from-[#f5b022]/40 to-transparent" />
                </div>
                {(section.label !== "TESTES" || testesAberto) && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {section.cards.map((c) => {
                    const i = cardIndex++;
                    const Icon = ICON_MAP[c.icon.name] ?? c.icon;
                    return (
                      <Link
                        key={c.route}
                        to={c.route}
                        className="group relative rounded-2xl border p-6 shadow-xl transition-all duration-300 animate-fade-in overflow-hidden backdrop-blur-sm border-white/10 bg-white/5 hover:shadow-2xl hover:border-[#f5b022]/50 hover:bg-white/[0.08]"
                        style={{ animationDelay: `${1.65 + i * 0.05}s`, animationFillMode: "backwards" }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-[#f5b022]/0 via-transparent to-[#f5b022]/0 group-hover:from-[#f5b022]/10 group-hover:to-transparent transition-all duration-500" />
                        {c.beta && (
                          <span className="absolute top-3 right-3 text-[9px] font-semibold tracking-widest uppercase text-white/50 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                            Beta
                          </span>
                        )}
                        <div className="relative">
                          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-[#f5b022]/20 to-[#f5b022]/5 border border-[#f5b022]/20 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                            <Icon className="h-6 w-6 text-admin-accent" />
                          </div>
                          <h2 className="text-base font-bold text-white tracking-wide">{c.title}</h2>
                          <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{c.desc}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
